import { onlineManager } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";
import { retryQuery } from "./queryClient";

/**
 * Whether the browser says it is offline: TanStack's online manager (which follows the window's
 * `online` and `offline` events) or, before its first event, `navigator.onLine`.
 */
export function isOffline(): boolean {
  if (!onlineManager.isOnline()) return true;
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

/** `isOffline` as React state, so an observer or a timer can stand down while offline. */
export function useIsOnline(): boolean {
  return useSyncExternalStore(
    (onChange) => onlineManager.subscribe(onChange),
    () => !isOffline(),
    () => true,
  );
}

/**
 * #1398: the one rule for a LIST query, spread into its `useQuery`, `useInfiniteQuery` or next-page
 * `fetchQuery` options. Covered: Connections (and a connection's tables), Schedules, Runs (a
 * connection's Runs tab, the run log), Transformations (its jobs, runs, Load more and per-model
 * history), the Audit trail and its reports, Users, schema changes, skyMonitor's checks and
 * incidents and the Platform instances list.
 *
 * The rule for a screen on a covered list: its full error only for a failed FIRST read; a failed
 * re-read (offline, a Retry, an invalidation) keeps the rows and says so. `RefreshErrorBar` does it
 * on Schedules, skyMonitor (Tables, Schedules, Incidents), Platform instances and the Connector pins
 * card, Users, the Audit trail and its reports, the tenant audit trail and the dbt jobs list; the
 * Runs list, a connection's Runs tab, the run log, the Connections list, the schedule history and
 * the Transformations history and per-model history already did. To check against it (other
 * changes own those folders): the Overview's recent runs card (`components/overview/`, its error
 * status) and the connection editor's schema changes drawer.
 *
 * Not covered, so they keep TanStack's pause offline and their rows: the tenants list
 * (`useTenants`), whose own `refetchInterval` follows a tenant's setup on the same key and would
 * fail every tick; and the Platform connectors list, alert deliveries, AI app grants and the
 * deleted tenants, whose screens replace their rows with a full error on any failed read. None of
 * these uses `keepPreviousData`, so paused they never show another filter's rows.
 *
 * TanStack's default `networkMode` ("online") PAUSES a query while the browser reports offline: no
 * request leaves, `apiRequest`'s "We could not reach skyData" never runs, and a list asked for a new
 * filter sits pending behind `keepPreviousData`, showing the previous filter's rows as if they
 * matched. "always" sends the request, so it fails into that message and the list's error state.
 * TanStack shows placeholder data only while a query is pending, so once it fails the old rows go.
 *
 * "always" also turns TanStack's `refetchOnReconnect` default off, so it is set back on: a list that
 * failed offline, or went stale, reads again by itself when the browser is back online. A hook that
 * never refetches on reconnect sets `false` after the spread.
 *
 * Offline a retry would only hold the old rows on screen through its backoff, so a list does not
 * retry then; online it retries as every query does (`retryQuery`). A hook with its own `retry`
 * sets it after the spread. Every observer of a list's key takes this too: one key, one rule, since
 * the mode of whichever read runs last applies to the shared entry.
 *
 * A poll on a list key would now fail every tick offline, so every poll stands down while offline:
 * `usePollWhile` skips its callback, and an observer with a `refetchInterval` is disabled through
 * `useIsOnline`. Every query that is not a list keeps TanStack's pause.
 */
export const listQueryOptions = {
  networkMode: "always",
  refetchOnReconnect: true,
  retry: (failureCount: number, error: unknown): boolean =>
    !isOffline() && retryQuery(failureCount, error),
} as const;
